import { observer } from "mobx-react"
import { Tag, Typography } from "antd"
import { ColumnsType } from "antd/es/table"

import { NoContent } from "@src/app/components/NoContent/Index"
import { GlobalCurrenciesCollection } from "@src/app/CurrenciesCollection"
import {
  NetworkCrypto,
  PayoutResponseStatusEnum,
  SymbolCrypto,
} from "@src/shared/api/generated"
import { DateTimeTableCell } from "@src/shared/componemts/DateTimeTableCell"
import { TableExpandable } from "@src/shared/componemts/TableExpandable"
import {
  filtersPresets,
  getFilterSimpleFiled,
} from "@src/shared/componemts/TableFilter/classes/constants"
import { WithCopy } from "@src/shared/componemts/WithCopy"
import { dateTimeFormat } from "@src/shared/date"
import { paddingLimit } from "@src/shared/strings"
import { CryptoPayout } from "./types"

export const TransactionsOutFilters = [
  getFilterSimpleFiled("externalId").search({
    label: "External ID",
    placeholder: "Search for External ID",
  }),
  getFilterSimpleFiled("address").search({
    placeholder: "Search for destination address",
  }),
  getFilterSimpleFiled("network").select(
    Object.values(NetworkCrypto).map((value) => ({ label: value, value })),
    { placeholder: "Select a network" },
  ),
  getFilterSimpleFiled("symbol").select(
    Object.values(SymbolCrypto).map((value) => ({ label: value, value })),
    { label: "Symbol", placeholder: "Select a symbol" },
  ),
  getFilterSimpleFiled("status").select(
    Object.values(PayoutResponseStatusEnum).map((value) => ({
      label: value,
      value,
    })),
    { placeholder: "Select a status" },
  ),
  getFilterSimpleFiled("type").select(
    [
      { label: "Payout", value: "payout" },
      { label: "Withdrawal", value: "withdrawal" },
    ],
    { placeholder: "Select a type" },
  ),
  getFilterSimpleFiled("extTxId").search({
    label: "Tx ID",
    placeholder: "Search for Tx ID",
  }),
  getFilterSimpleFiled("amlLevel").select(
    ["low", "medium", "high"].map((value) => ({ label: value, value })),
    { label: "AML Level", placeholder: "Select an AML level" },
  ),
  getFilterSimpleFiled("date").date({ label: "Date" }),
  getFilterSimpleFiled("massPayoutId").search({
    label: "Mass payout ID",
    placeholder: "Mass payout ID",
  }),
  filtersPresets.order,
]

const copyValue = (value?: string, shorten = false) =>
  value ? (
    <WithCopy
      content={value}
      view={
        <Typography.Text>
          {shorten ? paddingLimit(value, 0, 3) : value}
        </Typography.Text>
      }
    />
  ) : (
    "—"
  )

const PayoutTxId = observer(
  ({
    payout,
    shorten = false,
  }: {
    payout: CryptoPayout
    shorten?: boolean
  }) => {
    const { ext_tx_id, network, symbol } = payout.data
    const currency = GlobalCurrenciesCollection().getBySymbolNetwork({
      network,
      symbol,
    })

    if (!ext_tx_id) return <>—</>

    if (!currency?.tx_explorer_url) return <>{copyValue(ext_tx_id, shorten)}</>

    return (
      <WithCopy
        content={ext_tx_id}
        view={
          <Typography.Link
            href={`${currency.tx_explorer_url}${ext_tx_id}`}
            rel="noopener noreferrer"
            target="_blank"
          >
            {shorten ? paddingLimit(ext_tx_id, 0, 3) : ext_tx_id}
          </Typography.Link>
        }
      />
    )
  },
)

const statusTag = (status: string) => (
  <Tag
    color={
      {
        cancelled: "default",
        completed: "green",
        failed: "error",
        new: "geekblue",
        pending: "geekblue",
      }[status]
    }
  >
    {status}
  </Tag>
)

const amlTag = (payout: CryptoPayout) => {
  const level = payout.data.aml_level ?? payout.aml_level
  return level ? (
    <Tag color={{ high: "error", low: "green", medium: "warning" }[level]}>
      {level}
    </Tag>
  ) : (
    "—"
  )
}

export const TransactionsOutColumns: ColumnsType<CryptoPayout> = [
  { dataIndex: "id", render: (_, { id }) => copyValue(id, true), title: "ID" },
  {
    dataIndex: ["data", "address"],
    render: (_, { data }) => copyValue(data.address, true),
    title: "Address",
  },
  {
    dataIndex: ["data", "ext_tx_id"],
    render: (_, payout) => <PayoutTxId payout={payout} shorten />,
    title: "Tx ID",
  },
  {
    dataIndex: ["data", "amount"],
    render: (_, { data }) => (
      <Typography.Text strong>{data.amount}</Typography.Text>
    ),
    title: "Amount",
  },
  { dataIndex: ["data", "network"], title: "Network" },
  { dataIndex: ["data", "symbol"], title: "Symbol" },
  {
    dataIndex: "status",
    render: (_, { status }) => statusTag(status),
    title: "Status",
  },
  {
    key: "aml_level",
    render: (_, payout) => amlTag(payout),
    title: "AML Level",
  },
  {
    dataIndex: "created_at",
    render: (_, { created_at }) =>
      created_at ? <DateTimeTableCell value={created_at} /> : "—",
    title: "Created at",
  },
]

export const TransactionsOutDetails = ({
  payout,
}: {
  payout: CryptoPayout
}) => {
  const { callback_url, created_at, data, error, external_id, id, status } =
    payout

  const { fees, from_fiat } = data
  const list: [string, React.ReactNode][] = [
    ["ID", copyValue(id)],
    ["Address", copyValue(data.address)],
    ["Tx ID", <PayoutTxId payout={payout} />],
    ["Amount", data.amount],
    ["Network", data.network],
    ["Symbol", data.symbol],
    ["Status", statusTag(status)],
    ["AML Level", amlTag(payout)],
    ["Created at", created_at ? dateTimeFormat(created_at) : "—"],
    ["External ID", copyValue(external_id)],
    ["Mass payout ID", copyValue(payout.mass_payout_id)],
    ["Callback URL", copyValue(callback_url)],
  ]

  if (from_fiat)
    list.push(["From fiat", `${from_fiat.amount} ${from_fiat.symbol}`])

  if (error) {
    Object.entries(error).forEach(([key, value]) =>
      list.push([`Error — ${key}`, String(value ?? "—")]),
    )
  }

  const feeAmount = (amount?: string) =>
    amount !== undefined ? `${amount} ${data.symbol}` : "—"

  list.push(["Total fee", feeAmount(fees?.total_fee)])
  if (fees?.fix) {
    list.push(["Fee - fixed amount", feeAmount(fees.fix.amount)])
  }

  if (fees?.percent) {
    list.push([
      `Fee - Percent amount${fees.percent.value !== undefined ? ` (${fees.percent.value}%)` : ""}`,
      feeAmount(fees.percent.amount),
    ])
  }

  return <TableExpandable list={list} />
}

export const TransactionsOutEmpty = () => (
  <NoContent
    paragraph="Wait for your first transaction to start receiving reports."
    title="No available payouts"
  />
)
